import { useId } from 'react'

export interface NumericCellProps {
  label: string
  value: string
  onChange: (value: string) => void
  /** Whole birds and eggs. Feed quantities and money are decimals. */
  integer?: boolean
  min?: number
  placeholder?: string
  disabled?: boolean
  hint?: string
}

/**
 * A numeric entry cell that keeps its value as a *string* all the way to the API.
 *
 * Money and quantities are never parsed into a JS number in this app (§3.10).
 * The browser is where a `type="number"` input would quietly turn "0.1" into a
 * float, and the farm's feed costs would inherit the error.
 */
export function NumericCell({
  label,
  value,
  onChange,
  integer = false,
  min = 0,
  placeholder,
  disabled,
  hint,
}: NumericCellProps) {
  const id = useId()
  const pattern = integer ? '\\d*' : '\\d*(\\.\\d*)?'

  return (
    <label className="cell" htmlFor={id}>
      <span className="cell-label">{label}</span>
      <input
        id={id}
        className="cell-input numeric"
        type="text"
        inputMode={integer ? 'numeric' : 'decimal'}
        pattern={pattern}
        value={value}
        min={min}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(event) => {
          const next = event.target.value
          // Reject anything that is not a partial number, so the field can never
          // hold a value the API will reject on submit.
          if (next === '' || new RegExp(`^${pattern}$`).test(next)) onChange(next)
        }}
      />
      {hint ? <span className="cell-hint">{hint}</span> : null}
    </label>
  )
}
